import { rectFromPoints } from '../core/types';
import {
  BRAILLE_COLS,
  BRAILLE_ROWS,
  BrailleSurface,
  dotAt,
} from '../unicode/braille';
import { plotEllipse, plotLine, plotRect } from './raster';
import { CellWrite, Tool, ToolEnv, ToolStroke, paintCell } from './types';

/**
 * Braille drawing tools.
 *
 * All four share one pipeline: build a dot surface over the affected cells,
 * seed it from the Braille glyphs already on the canvas so strokes accumulate,
 * rasterise the shape in dot space, then write back only the cells whose glyph
 * changed.
 */

interface DotPoint {
  dx: number;
  dy: number;
}

function strokeDots(stroke: ToolStroke): DotPoint[] {
  return stroke.fracPoints.map((p) => dotAt(p.x, p.y, p.fx, p.fy));
}

function startDot(stroke: ToolStroke): DotPoint {
  return dotAt(stroke.start.x, stroke.start.y, stroke.startFrac.x, stroke.startFrac.y);
}

function endDot(stroke: ToolStroke): DotPoint {
  return dotAt(stroke.current.x, stroke.current.y, stroke.currentFrac.x, stroke.currentFrac.y);
}

/**
 * Builds a surface covering the whole document.
 *
 * A document-sized surface keeps the code simple and is cheap: the dot array is
 * one byte per dot, so even a 300x150 document is well under 1 MB and only the
 * changed cells are written back.
 */
function surfaceFor(env: ToolEnv): BrailleSurface {
  const s = new BrailleSurface(env.doc.width, env.doc.height, 0, 0);
  s.loadFrom((x, y) => env.read(x, y).char);
  return s;
}

function writeBack(surface: BrailleSurface, env: ToolEnv): CellWrite[] {
  const out: CellWrite[] = [];
  for (const cell of surface.changedCells()) {
    if (!env.selection.allows(cell.x, cell.y)) continue;
    const existing = env.read(cell.x, cell.y);
    // An emptied glyph becomes a blank cell rather than U+2800, which would
    // otherwise leave invisible "blank braille" litter behind the eraser.
    const char = cell.mask === 0 ? '' : cell.char;
    out.push({
      x: cell.x,
      y: cell.y,
      cell:
        char === ''
          ? { char: '', fg: existing.fg, bg: existing.bg, attrs: existing.attrs }
          : paintCell(existing, env.brush, char),
    });
  }
  return out;
}

export const brailleTool: Tool = {
  id: 'braille',
  name: 'Braille Pencil',
  group: 'draw',
  shortcut: 'D',
  hint: `Draw at ${BRAILLE_COLS}x${BRAILLE_ROWS} sub-cell resolution. Right-drag erases dots.`,
  freehand: true,
  plan(stroke, env) {
    const surface = surfaceFor(env);
    const on = !(env.brush.brailleErase || stroke.button === 2 || stroke.alt);
    const dots = strokeDots(stroke);
    if (dots.length === 1) {
      surface.set(dots[0].dx, dots[0].dy, on);
    } else {
      for (let i = 1; i < dots.length; i++) {
        plotLine(dots[i - 1].dx, dots[i - 1].dy, dots[i].dx, dots[i].dy, (x, y) =>
          surface.set(x, y, on),
        );
      }
    }
    return writeBack(surface, env);
  },
};

export const brailleLineTool: Tool = {
  id: 'brailleLine',
  name: 'Braille Line',
  group: 'draw',
  hint: 'Straight sub-cell line.',
  plan(stroke, env) {
    const surface = surfaceFor(env);
    const on = !(env.brush.brailleErase || stroke.button === 2);
    const a = startDot(stroke);
    const b = endDot(stroke);
    plotLine(a.dx, a.dy, b.dx, b.dy, (x, y) => surface.set(x, y, on));
    return writeBack(surface, env);
  },
};

export const brailleRectTool: Tool = {
  id: 'brailleRect',
  name: 'Braille Rectangle',
  group: 'draw',
  hint: 'Sub-cell rectangle. Hold Alt to fill.',
  plan(stroke, env) {
    const surface = surfaceFor(env);
    const on = !(env.brush.brailleErase || stroke.button === 2);
    const a = startDot(stroke);
    const b = endDot(stroke);
    const r = rectFromPoints({ x: a.dx, y: a.dy }, { x: b.dx, y: b.dy });
    plotRect(r.x, r.y, r.width, r.height, (x, y) => surface.set(x, y, on), env.brush.filled || stroke.alt);
    return writeBack(surface, env);
  },
};

export const brailleEllipseTool: Tool = {
  id: 'brailleEllipse',
  name: 'Braille Ellipse',
  group: 'draw',
  hint: 'Sub-cell ellipse. Hold Shift for a visually round circle, Alt to fill.',
  plan(stroke, env) {
    const surface = surfaceFor(env);
    const on = !(env.brush.brailleErase || stroke.button === 2);
    const a = startDot(stroke);
    const b = endDot(stroke);
    let r = rectFromPoints({ x: a.dx, y: a.dy }, { x: b.dx, y: b.dy });
    if (stroke.shift) {
      // A dot is 1/2 cell wide and 1/4 cell tall, so a visually round circle
      // needs a box whose dot-height is twice its dot-width.
      const w = r.width;
      const h = w * 2;
      r = { x: r.x, y: r.y, width: w, height: h };
      if (b.dy < a.dy) r.y = a.dy - h + 1;
    }
    plotEllipse(r.x, r.y, r.width, r.height, (x, y) => surface.set(x, y, on), env.brush.filled || stroke.alt);
    return writeBack(surface, env);
  },
};
